/* eslint-disable @typescript-eslint/naming-convention */

import { DropdownOption } from "../../../shared/components/dynamic-form/question-dropdown";
import { SearchFilterOption } from "../../../shared/components/search-bar/search-filter/search-filter.component";
import { SearchInputOption } from "../../../shared/components/search-bar/search-input/search-input.component";
import { AnyObject } from "../../../shared/types/app.type";
import { UserInfo } from "../../../shared/types/user-info";
import { setFilterCondition } from "../../../shared/utils/helpers/common.helper";
import { CompanyService } from "../companies/company.service";
import { ConfigurableOptionService } from "../configurable-options/configurable-options.service";
import { PartnerService } from "../partners/partner.service";

import {
  Asset,
  AssetAttachment,
  AssetAttribute,
  AssetCategories,
  AssetCoverageStatuses,
  AssetQuotedTerms,
  ContractAsset,
} from "./asset.type";

//#region Search Filter Options

export const getAssetSearchFilterOptions = (
  user: UserInfo,
  config?: {
    shown?: string[];
    hidden?: string[];
    companyService?: CompanyService;
    partnerService?: PartnerService;
    configurableOptionService?: ConfigurableOptionService;
  },
): SearchFilterOption<Asset>[] => {
  const { shown, hidden, companyService, partnerService, configurableOptionService } = config || {};

  return (
    [
      { value: "name", label: "Name" },
      { value: "serialNumber", label: "Serial #" },
      {
        auxValue: "slaId",
        inputType: "dropdown",
        condition: "$eq",
        value: "sla.value",
        label: "SLA",
        filterByProperty: { slaId: true },
        asyncOptions: ({ queryParams }) => {
          const where = { value: queryParams["name"] };
          return configurableOptionService.getSearchableOptions({
            where: {
              ...(where && { ...setFilterCondition(where as AnyObject, "$cont") }),
            },
          });
        },
        search: {
          callback: ({ queryParams }) => {
            const where = { value: queryParams["name"] };
            return configurableOptionService.getSearchableOptions({
              where: {
                ...(where && {
                  ...setFilterCondition(where as AnyObject, "$cont"),
                }),
              },
            });
          },
        },
      },
      {
        value: "category",
        label: "Category",
        inputType: "dropdown",
        condition: "$eq",
        options: [...getAssetCategories()],
      },
      {
        value: "type.model.oem.name",
        label: "OEM",
        filterByProperty: { type: { model: { oem: { name: true } } } },
      },
      {
        value: "type.model.name",
        label: "Model #",
        filterByProperty: { type: { model: { name: true } } },
      },
      {
        value: "type.name",
        label: "Type",
        filterByProperty: { type: { name: true } },
      },
      {
        auxValue: "partnerId",
        value: "company.partner.name",
        label: "Partner",
        hidden: !user.isGlobal() || !partnerService,
        filterByProperty: { company: { partner: { name: true } } },
      },
      {
        auxValue: "companyId",
        value: "company.name",
        label: "Company",
        hidden: user.isCompanyOrViewAsCompany({ ignoreIfMultiCompanies: true }) || !companyService,
        filterByProperty: { company: { name: true } },
      },
      {
        value: "coverageStatus",
        label: "Coverage Status",
        inputType: "dropdown",
        condition: "$eq",
        options: [...getAssetCoverageStatuses()],
      },
      {
        auxValue: "internalProviderId",
        value: "internalProvider.name",
        label: "Internal Provider",
        hidden: !user.isGlobal(),
        filterByProperty: { internalProvider: { name: true } },
      },
    ] as SearchFilterOption<Asset>[]
  ).filter((opt) => (shown ? shown.includes(opt.value) : true) && (hidden ? !hidden.includes(opt.value) : true));
};

export const getContractAssetSearchFilterOptions = (
  user: UserInfo,
  config?: {
    shown?: string[];
    hidden?: string[];
    companyService?: CompanyService;
    partnerService?: PartnerService;
    configurableOptionService?: ConfigurableOptionService;
  },
): SearchFilterOption<ContractAsset>[] => {
  const { shown, hidden, companyService, partnerService, configurableOptionService } = config || {};

  return (
    [
      { value: "asset.name", label: "Name", filterByProperty: { asset: { name: true } } },
      { value: "asset.serialNumber", label: "Serial #", filterByProperty: { asset: { serialNumber: true } } },
      {
        auxValue: "slaId",
        inputType: "dropdown",
        condition: "$eq",
        value: "sla.value",
        label: "SLA",
        filterByProperty: { slaId: true },
        asyncOptions: ({ queryParams }) => {
          const where = { value: queryParams["name"] };
          return configurableOptionService.getSearchableOptions({
            where: {
              ...(where && { ...setFilterCondition(where as AnyObject, "$cont") }),
            },
          });
        },
        search: {
          callback: ({ queryParams }) => {
            const where = { value: queryParams["name"] };
            return configurableOptionService.getSearchableOptions({
              where: {
                ...(where && {
                  ...setFilterCondition(where as AnyObject, "$cont"),
                }),
              },
            });
          },
        },
      },
      {
        value: "asset.category",
        label: "Category",
        inputType: "dropdown",
        condition: "$eq",
        options: [...getAssetCategories()],
        filterByProperty: { asset: { category: true } },
      },
      {
        value: "asset.type.model.oem.name",
        label: "OEM",
        filterByProperty: { asset: { type: { model: { oem: { name: true } } } } },
      },
      {
        value: "asset.type.model.name",
        label: "Model #",
        filterByProperty: { asset: { type: { model: { name: true } } } },
      },
      {
        value: "asset.type.name",
        label: "Type",
        filterByProperty: { asset: { type: { name: true } } },
      },
      {
        auxValue: "asset.partnerId",
        value: "company.partner.name",
        label: "Partner",
        hidden: !user.isGlobal() || !partnerService,
        filterByProperty: { asset: { company: { partner: { name: true } } } },
      },
      {
        auxValue: "asset.companyId",
        value: "company.name",
        label: "Company",
        hidden: user.isCompanyOrViewAsCompany({ ignoreIfMultiCompanies: true }) || !companyService,
        filterByProperty: { asset: { company: { name: true } } },
      },
      {
        value: "coverageStatus",
        label: "Coverage Status",
        inputType: "dropdown",
        condition: "$eq",
        options: [...getAssetCoverageStatuses()],
      },
      {
        auxValue: "internalProviderId",
        value: "internalProvider.name",
        label: "Internal Provider",
        hidden: !user.isGlobal(),
        filterByProperty: { internalProvider: { name: true } },
      },
    ] as SearchFilterOption<ContractAsset>[]
  ).filter((opt) => (shown ? shown.includes(opt.value) : true) && (hidden ? !hidden.includes(opt.value) : true));
};

//#endregion

//#region Search Input Options

export const getAssetCategories = (): SearchInputOption[] => {
  return Object.keys(AssetCategories).map((key) => {
    const value = AssetCategories[key];
    return { value, label: capitalizeFirstLetter(key) };
  });
};

export const getAssetCoverageStatuses = (): SearchInputOption[] => {
  const customLabels: { [key in AssetCoverageStatuses]?: string } = {
    "not covered": "Not Covered",
    "replaced during repair": "Replaced During Repair",
  };
  return Object.keys(AssetCoverageStatuses).map((key) => {
    const value = AssetCoverageStatuses[key];
    const label = customLabels[value] || capitalizeFirstLetter(key);
    return { value, label };
  });
};

export const assetAttributeTypes: SearchInputOption[] = [
  { label: "MIPs", value: "MIPs", auxValue: AssetCategories.mainframe },
  { label: "MSU", value: "MSU", auxValue: AssetCategories.mainframe },
  { label: "Supervisor Engine", value: "Supervisor Engine", auxValue: AssetCategories.networking },
  { label: "Route Processor", value: "Route Processor", auxValue: AssetCategories.networking },
  { label: "Port", value: "Port", auxValue: AssetCategories.networking },
  { label: "Fabric Module", value: "Fabric Module", auxValue: AssetCategories.networking },
  { label: "ESP Cards", value: "ESP Cards", auxValue: AssetCategories.networking },
  { label: "SPA Cards", value: "SPA Cards", auxValue: AssetCategories.networking },
  { label: "Line Cards", value: "Line Cards", auxValue: AssetCategories.networking },
  { label: "NM Cards", value: "NM Cards", auxValue: AssetCategories.networking },
  { label: "Internal Disk", value: "Internal Disk", auxValue: AssetCategories.server },
  { label: "Memory", value: "Memory", auxValue: AssetCategories.server },
  { label: "CPU", value: "CPU", auxValue: AssetCategories.server },
  { label: "Disk", value: "Disk", auxValue: AssetCategories.storage },
];

export const getAssetActions = (): SearchFilterOption[] => [
  { value: "Dispose", label: "Dispose", disabled: false },
  { value: "Extend", label: "Extend", disabled: false },
  { value: "Maintain", label: "Maintain", disabled: false },
  { value: "Upgrade", label: "Upgrade", disabled: false },
  { value: "Sell", label: "Sell", disabled: false },
  { value: "ModifyContractTerms", label: "Modify Contract Terms", disabled: false },
  { value: "Return", label: "Return", disabled: false },
];

//#endregion

export const getAssetQuotedTerms = (): DropdownOption[] => {
  const assetQuotedTerms: DropdownOption[] = [];

  Object.keys(AssetQuotedTerms).forEach((key) => {
    const value = AssetQuotedTerms[key];
    assetQuotedTerms.push({ value, label: capitalizeFirstLetter(key) });
  });

  return assetQuotedTerms;
};

export const getDisplayedColumnsAssignAssets = (currentUser: UserInfo): string[] => {
  const columns = [
    "name",
    "serialNumber",
    "coverageStatus",
    "contract.name",
    "location.name",
    "sla.value",
    "startDate",
    "endDate",
    "dmr",
    "monitoring",
  ];
  return currentUser?.isGlobal() ? [...columns, "channelWorksCost", "partnerPrice"] : columns;
};

//#region Search Filter Options

export const getAssetAttachmentSearchFilterOptions = (
  _user: UserInfo,
  config?: {
    shown?: string[];
    hidden?: string[];
  },
): SearchFilterOption<AssetAttachment>[] => {
  const { shown, hidden } = config || {};

  return (
    [
      { value: "extension", label: "File Type" },
      { value: "name", label: "Name" },
      { value: "description", label: "Description" },
    ] as SearchFilterOption<AssetAttachment>[]
  ).filter((opt) => (shown ? shown.includes(opt.value) : true) && (hidden ? !hidden.includes(opt.value) : true));
};

export const getAssetAttributeSearchFilterOptions = (
  _user: UserInfo,
  config?: {
    shown?: string[];
    hidden?: string[];
  },
): SearchFilterOption<AssetAttribute>[] => {
  const { shown, hidden } = config || {};

  return (
    [
      { value: "attribute.name", label: "Attribute Name", filterByProperty: { attribute: { name: true } } },
      { value: "attribute.type", label: "Attribute Type", filterByProperty: { attribute: { type: true } } },
      { value: "attribute.buyPrice", label: "Buy Price", filterByProperty: { attribute: { buyPrice: true } } },
      { value: "quantity", label: "Quantity" },
    ] as SearchFilterOption<AssetAttribute>[]
  ).filter((opt) => (shown ? shown.includes(opt.value) : true) && (hidden ? !hidden.includes(opt.value) : true));
};

const capitalizeFirstLetter = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);

//#endregion
